import { FileDown, Printer, Table2 } from "lucide-react";
import { Badge, Button, Card, EmptyState, SectionHeading } from "./ui";
import QuoteComparison from "./QuoteComparison";
import type { BudgetItemDto, PortalPayload, QuoteDto } from "../lib/api";
import { formatINR, percent } from "../lib/format";

function SummaryCard({
  label,
  value,
  note,
  tone = "neutral",
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "neutral" | "accent" | "success" | "warning";
}) {
  const border =
    tone === "accent"
      ? "border-wandor-prompt/30"
      : tone === "success"
        ? "border-emerald-400/30"
        : tone === "warning"
          ? "border-amber-400/30"
          : "border-white/10";
  return (
    <Card className={`p-6 ${border}`}>
      <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-white">{value}</p>
      {note && <p className="mt-1 text-sm text-wandor-muted">{note}</p>}
    </Card>
  );
}

/** Accessible donut chart built as inline SVG, paired with a numeric table. */
function DonutChart({
  segments,
  total,
  centerLabel,
  centerValue,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  total: number;
  centerLabel: string;
  centerValue: string;
}) {
  const size = 220;
  const radius = 84;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const safeTotal = Math.max(total, 1);

  return (
    <div className="flex flex-col items-center gap-6 md:flex-row">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`${centerLabel}: ${centerValue}`}
        className="shrink-0"
      >
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#efe9e3" strokeWidth="26" />
        {segments.map((seg) => {
          const fraction = seg.value / safeTotal;
          const dash = fraction * circumference;
          const el = (
            <circle
              key={seg.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth="26"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          offset += dash;
          return el;
        })}
        <text x="50%" y="47%" textAnchor="middle" className="fill-wandor-muted text-[11px]">
          {centerLabel}
        </text>
        <text x="50%" y="58%" textAnchor="middle" className="fill-white text-[15px] font-semibold">
          {centerValue}
        </text>
      </svg>
      <ul className="flex-1 space-y-2">
        {segments.map((seg) => (
          <li key={seg.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full" style={{ background: seg.color }} aria-hidden="true" />
              <span className="text-wandor-text">{seg.label}</span>
            </span>
            <span className="tabular-nums text-wandor-muted">
              {formatINR(seg.value)} · {percent((seg.value / safeTotal) * 100)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const PALETTE = ["#905831", "#1a1a1a", "#c08a5e", "#767676", "#d9c3aa", "#5c4632", "#a8a29e", "#e0b28c"];

export default function BudgetDashboard({
  data,
  quotes,
}: {
  data: PortalPayload;
  quotes: QuoteDto[];
}) {
  const s = data.budgetSummary;
  const items = data.budgetItems;
  const byCategory = new Map<number, { name: string; items: BudgetItemDto[] }>();
  for (const it of items) {
    const bucket = byCategory.get(it.categoryId) ?? { name: it.categoryName, items: [] };
    bucket.items.push(it);
    byCategory.set(it.categoryId, bucket);
  }

  const expenseSegments = s.perCategory.map((c, i) => ({
    label: c.name,
    value: c.estimatedPaise,
    color: PALETTE[i % PALETTE.length],
  }));

  const fundsSegments = [
    { label: "Expected student contributions", value: s.expectedContributionIncomePaise, color: "#905831" },
    { label: "Sponsorships & other funding", value: s.expectedSponsorshipPaise + s.expectedOtherIncomePaise, color: "#1a1a1a" },
    { label: s.fundingGapPaise > 0 ? "Funding gap (unfunded)" : "Projected surplus", value: Math.abs(s.fundingGapPaise), color: s.fundingGapPaise > 0 ? "#c0392b" : "#2f7d5b" },
  ];

  return (
    <section id="budget" className="mx-auto max-w-shell scroll-mt-24 px-5 py-24 md:px-10">
      <SectionHeading
        eyebrow="Budget & financial transparency"
        title="Every rupee, planned in the open"
        description="Planning estimates built from vendor quotations and committee assumptions. Figures marked “estimated” are not confirmed costs — actuals are recorded separately after the event."
      />

      <div className="no-print mt-6 flex flex-wrap items-center justify-center gap-3">
        <a href="/api/export/budget.csv" download>
          <Button variant="secondary" size="sm">
            <FileDown className="h-4 w-4" aria-hidden="true" /> Budget CSV
          </Button>
        </a>
        <a href="/api/export/budget-summary.csv" download>
          <Button variant="secondary" size="sm">
            <FileDown className="h-4 w-4" aria-hidden="true" /> Summary CSV
          </Button>
        </a>
        <Button variant="ghost" size="sm" onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden="true" /> Print report
        </Button>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="Total estimated expenses"
          value={formatINR(s.totalEstimatedPaise)}
          note="Sum of all planned line items"
        />
        <SummaryCard
          label="Contingency allowance"
          value={formatINR(s.contingencyAllowancePaise)}
          note="Buffer for unplanned costs (editable rate)"
          tone="warning"
        />
        <SummaryCard
          label="Projected total"
          value={formatINR(s.projectedTotalPaise)}
          note="Estimates + contingency — not a confirmed bill"
          tone="accent"
        />
        <SummaryCard
          label="Actual expenses (so far)"
          value={formatINR(s.totalActualPaise)}
          note="Recorded bills/receipts after the event"
          tone="success"
        />
        <SummaryCard
          label="Estimated food cost / attendee"
          value={data.budgetItems.some((x) => x.pricingHidden) ? "Held by committee" : formatINR(s.foodPerAttendeePaise)}
          note={
            data.budgetItems.some((x) => x.pricingHidden)
              ? "The food vendor rate is kept private by the committee."
              : `Food estimate ${formatINR(s.foodEstimatedPaise)} ÷ ${s.expectedAttendees} attendees`
          }
        />
        <SummaryCard
          label="Transport cost / passenger"
          value={formatINR(s.transportPerPassengerPaise)}
          note={`Transport ${formatINR(s.transportEstimatedPaise)} ÷ ${data.event.transportSeats} estimated passengers`}
        />
        <SummaryCard
          label="Projected cost / attendee"
          value={formatINR(s.costPerAttendeePaise)}
          note={`Projected total ÷ ${s.expectedAttendees} expected attendees`}
        />
        <SummaryCard
          label="Expected funds collected"
          value={formatINR(s.expectedIncomePaise)}
          note={`Contributions ${formatINR(s.expectedContributionIncomePaise)} + sponsorships ${formatINR(s.expectedSponsorshipPaise + s.expectedOtherIncomePaise)}`}
        />
      </div>

      <div className="mt-6">
        <Card className={`p-6 ${s.fundingGapPaise > 0 ? "border-amber-400/40" : "border-emerald-400/30"}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">
                {s.fundingGapPaise > 0 ? "Estimated funding gap" : "Estimated funding surplus"}
              </p>
              <p className="mt-1 text-3xl font-semibold text-white">
                {formatINR(Math.abs(s.fundingGapPaise))}
              </p>
              <p className="mt-1 text-sm text-wandor-muted">
                Based on expected contributions of {formatINR(data.event.contributionPaise)} per attendee.
                Recommended contribution to cover projected costs:{" "}
                <strong className="text-white">{formatINR(s.recommendedContributionPaise)}</strong> per attendee.
              </p>
            </div>
            <Badge tone={s.fundingGapPaise > 0 ? "warning" : "success"}>
              {s.fundingGapPaise > 0 ? "Projected shortfall" : "Projected fully funded"}
            </Badge>
          </div>
        </Card>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="print-card p-6 md:p-8">
          <h3 className="text-xl font-semibold text-white">Expense breakdown</h3>
          <p className="mt-1 text-sm text-wandor-muted">Estimated spend by budget category.</p>
          <div className="mt-6">
            <DonutChart
              segments={expenseSegments}
              total={s.totalEstimatedPaise}
              centerLabel="Total estimated"
              centerValue={formatINR(s.totalEstimatedPaise)}
            />
          </div>
        </Card>

        <Card className="print-card p-6 md:p-8">
          <h3 className="text-xl font-semibold text-white">Funds collected vs costs</h3>
          <p className="mt-1 text-sm text-wandor-muted">
            Expected funding sources compared with the projected total.
          </p>
          <div className="mt-6">
            <DonutChart
              segments={fundsSegments}
              total={s.expectedIncomePaise + Math.max(s.fundingGapPaise, 0)}
              centerLabel="Expected income"
              centerValue={formatINR(s.expectedIncomePaise)}
            />
          </div>
        </Card>
      </div>

      <div className="mt-12">
        <h3 className="flex items-center gap-2 text-xl font-semibold text-white">
          <Table2 className="h-5 w-5 text-wandor-prompt" aria-hidden="true" /> Estimated vs actual costs
        </h3>
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <SummaryCard label="Total estimated" value={formatINR(s.totalEstimatedPaise)} />
          <SummaryCard label="Total actual" value={formatINR(s.totalActualPaise)} tone="success" />
          <SummaryCard
            label="Difference (estimate − actual)"
            value={formatINR(s.totalEstimatedPaise - s.totalActualPaise)}
            note="Positive = under the estimate so far"
            tone="accent"
          />
        </div>
      </div>

      {quotes.length > 0 && (
        <QuoteComparison quotes={quotes} />
      )}

      <div className="mt-12">
        <h3 className="flex items-center gap-2 text-xl font-semibold text-white">
          <Table2 className="h-5 w-5 text-wandor-prompt" aria-hidden="true" /> Detailed cost table
        </h3>
        <p className="mt-1 text-sm text-wandor-muted">
          Tax treatment is recorded exactly as stated on vendor quotations — GST is never inferred from
          the payment channel. Unverified tax lines are flagged for the committee to confirm on the invoice.
        </p>

        {items.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              title="No budget line items yet"
              body="The committee will publish cost estimates here as vendor quotations come in."
            />
          </div>
        ) : (
          <>
            {/* Mobile: stacked cards */}
            <div className="mt-6 space-y-4 lg:hidden">
              {[...byCategory.values()].map((cat) => (
                <Card key={cat.name} className="p-5">
                  <h4 className="font-semibold text-white">{cat.name}</h4>
                  <ul className="mt-3 space-y-3">
                    {cat.items.map((it) => (
                      <li key={it.id} className="rounded-xl border border-white/8 p-3 text-sm">
                        <p className="font-medium text-wandor-text">{it.description}</p>
                        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-wandor-muted">
                          <dt>Quantity</dt>
                          <dd className="text-right tabular-nums">
                            {it.quantity} {it.unit}
                          </dd>
                          <dt>Unit cost</dt>
                          <dd className="text-right tabular-nums">{it.pricingHidden ? "—" : formatINR(it.unitCostPaise)}</dd>
                          <dt>Estimated</dt>
                          <dd className="text-right tabular-nums">
                            {it.pricingHidden ? "—" : formatINR(
                              it.estimatedPaise ?? it.quantity * it.unitCostPaise,
                            )}
                          </dd>
                          <dt>Actual</dt>
                          <dd className="text-right tabular-nums">{formatINR(it.actualPaise)}</dd>
                          <dt>Tax</dt>
                          <dd className="text-right">
                            {it.taxTreatment}
                            {it.gstRateBps ? ` ${it.gstRateBps / 100}%` : ""}
                          </dd>
                          <dt>Payment</dt>
                          <dd className="text-right">{it.paymentStatus}</dd>
                        </dl>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>

            {/* Desktop: real table */}
            <div className="mt-6 hidden overflow-x-auto lg:block">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">Detailed budget line items</caption>
                <thead>
                  <tr className="border-b border-white/15 text-xs uppercase tracking-wider text-wandor-muted">
                    <th scope="col" className="py-3 pr-4 font-medium">Category</th>
                    <th scope="col" className="py-3 pr-4 font-medium">Description</th>
                    <th scope="col" className="py-3 pr-4 font-medium">Qty</th>
                    <th scope="col" className="py-3 pr-4 font-medium">Unit cost</th>
                    <th scope="col" className="py-3 pr-4 font-medium">Type</th>
                    <th scope="col" className="py-3 pr-4 font-medium">Estimated</th>
                    <th scope="col" className="py-3 pr-4 font-medium">Actual</th>
                    <th scope="col" className="py-3 pr-4 font-medium">Tax</th>
                    <th scope="col" className="py-3 font-medium">Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-white/8 align-top">
                      <td className="py-3 pr-4 text-wandor-muted">{it.categoryName}</td>
                      <td className="py-3 pr-4 text-wandor-text">
                        {it.description}
                        {it.vendorRef && (
                          <span className="block text-xs text-wandor-muted">Quote: {it.vendorRef}</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 tabular-nums text-wandor-muted">
                        {it.quantity} {it.unit}
                      </td>
                      <td className="py-3 pr-4 tabular-nums text-wandor-muted">
                        {it.pricingHidden ? "—" : formatINR(it.unitCostPaise)}
                      </td>
                      <td className="py-3 pr-4 text-wandor-muted">
                        {it.isPerAttendee ? "Per attendee" : "Fixed"}
                      </td>
                      <td className="py-3 pr-4 tabular-nums font-medium text-white">
                        {it.pricingHidden ? "—" : formatINR(it.estimatedPaise ?? it.quantity * it.unitCostPaise)}
                      </td>
                      <td className="py-3 pr-4 tabular-nums text-wandor-text">{formatINR(it.actualPaise)}</td>
                      <td className="py-3 pr-4 text-wandor-muted">
                        {it.taxTreatment}
                        {it.gstRateBps ? ` ${it.gstRateBps / 100}%` : ""}
                        {it.taxTreatment === "unknown" && (
                          <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
                            verify on invoice
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-wandor-muted">{it.paymentStatus}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-white/20">
                    <td colSpan={5} className="py-3 font-semibold text-white">
                      Totals
                    </td>
                    <td className="py-3 pr-4 tabular-nums font-semibold text-white">
                      {formatINR(s.totalEstimatedPaise)}
                    </td>
                    <td className="py-3 pr-4 tabular-nums font-semibold text-white">
                      {formatINR(s.totalActualPaise)}
                    </td>
                    <td colSpan={2} className="py-3 text-wandor-muted">
                      Quoted/derived taxes: {formatINR(s.quotedTaxPaise)}
                      {s.taxUncertainCount > 0 &&
                        ` · ${s.taxUncertainCount} line(s) need invoice verification`}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>

      <p className="mt-8 text-xs leading-relaxed text-wandor-muted">
        Note: This website does not collect payments or process transactions. All contribution figures
        are planning estimates. Final amounts, vendor invoices, and tax treatment are confirmed by the
        organizing committee offline.
      </p>
    </section>
  );
}
